/**
 * The ONE way the console talks to tomo.
 *
 * Every call goes through `request()`. Nothing else in the app calls `fetch`,
 * for three reasons that each cost something to rediscover:
 *
 *   1. **`credentials: 'same-origin'` has to be on every request.** The
 *      session is a `__Host-` cookie; a single call that forgets it is
 *      anonymous and 401s for a reason nobody can see from the call site.
 *   2. **The problem envelope is parsed in one place.** The API answers
 *      failures with RFC 9457 problem+json carrying a stable `code`, and the
 *      whole point of that code is that clients branch on it rather than on a
 *      message.
 *   3. **Responses are validated against the published schema.** The same Zod
 *      objects the server generated the OpenAPI document from. A hand-written
 *      interface here would be a second definition free to drift — which is
 *      how the predecessors ended up with a rarity ladder copied into three
 *      modules, each silently keeping the old one.
 */
import type { z } from 'zod';
import { Problem, type ErrorCode } from '@tomo/contracts';

/**
 * A failure the API described.
 *
 * `code` is the contract, not `message`: an integrator — and this console is
 * one — branches on the code.
 */
export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode | 'network' | 'malformed_response',
    readonly status: number,
    message: string,
    readonly fields: { path: string; code: string; message: string }[] = [],
    readonly requestId?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field errors keyed by path, for putting a message next to an input. */
  byField(): Record<string, string> {
    return Object.fromEntries(this.fields.map((f) => [f.path, f.message]));
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  opts: RequestOptions = {}
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: opts.method ?? 'GET',
      // Not optional, and not per call site. See above.
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(opts.body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch {
    // A dropped connection is not a 500 and must not be reported as one: the
    // difference is "try again" versus "something is broken".
    throw new ApiError('network', 0, 'Could not reach tomo. Check your connection.');
  }

  const text = await res.text();
  const payload: unknown = text ? safeJson(text) : null;

  if (!res.ok) {
    const problem = Problem.safeParse(payload);
    if (problem.success) {
      throw new ApiError(
        problem.data.code as ErrorCode,
        res.status,
        problem.data.detail ?? problem.data.title,
        problem.data.errors ?? [],
        problem.data.request_id
      );
    }
    // A non-problem error body means something in front of the API answered —
    // a proxy, the load balancer, a crash before the handler. Say so rather
    // than inventing a code.
    throw new ApiError(
      'malformed_response', res.status,
      `The server answered ${String(res.status)} in a shape tomo does not use.`
    );
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    // The published contract and the server disagree. Loud, because the
    // alternative is a screen that renders undefined and looks merely empty.
    throw new ApiError(
      'malformed_response', res.status,
      'The response did not match the published schema.',
      parsed.error.issues.map((i) => ({
        path: i.path.join('.'), code: i.code, message: i.message,
      }))
    );
  }
  return parsed.data;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Cursor pages, walked to the end. Catalogs are tens of rows, not millions. */
export async function requestAll<T>(
  path: string,
  pageSchema: z.ZodType<{ data: T[]; next_cursor: string | null }>,
  signal?: AbortSignal
): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | null = null;
  // Bounded. An endpoint that kept handing back a cursor would otherwise spin
  // the browser forever, and a console that hangs is worse than one that
  // shows the first thousand rows.
  for (let page = 0; page < 20; page++) {
    const sep = path.includes('?') ? '&' : '?';
    const url: string = cursor ? `${path}${sep}cursor=${encodeURIComponent(cursor)}` : path;
    const result: { data: T[]; next_cursor: string | null } =
      await request(url, pageSchema, signal ? { signal } : {});
    out.push(...result.data);
    cursor = result.next_cursor;
    if (!cursor) break;
  }
  return out;
}
